import { useState, useRef, useEffect } from 'react';
import { MapPin, Buildings, CaretDown, Globe } from '@phosphor-icons/react';
import { useLocationContext } from '../contexts/LocationContext';

const TYPE_ICON = {
  factory: '🏭',
  warehouse: '🏪',
  office: '🏢',
  branch: '🏬',
};

const TYPE_COLOR = {
  factory: 'hsl(197,100%,48%)',
  warehouse: 'hsl(43,96%,56%)',
  office: 'hsl(142,70%,45%)',
  branch: 'hsl(280,60%,60%)',
};

export default function LocationSwitcher() {
  const { locations, activeLocation, switchLocation, isGlobal } = useLocationContext();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Don't render if only 1 location and no global access
  if (locations.length === 0) return null;
  if (locations.length === 1 && !isGlobal) return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 5,
      fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
      color: 'hsl(0,0%,55%)', textTransform: 'uppercase', letterSpacing: '0.08em',
      flexShrink: 0,
    }}>
      <MapPin size={12} color={TYPE_COLOR[locations[0]?.type] || 'hsl(197,100%,48%)'} weight="fill" />
      <span className="loc-label">{locations[0]?.name}</span>
    </div>
  );

  const label = activeLocation
    ? `${TYPE_ICON[activeLocation.type] || '📍'} ${activeLocation.name}`
    : '🌐 All Locations';

  const activeColor = activeLocation
    ? (TYPE_COLOR[activeLocation.type] || 'hsl(197,100%,48%)')
    : 'hsl(0,0%,50%)';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(v => !v)}
        className="loc-switcher-btn"
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: open ? 'hsl(0,0%,10%)' : 'transparent',
          border: '1px solid',
          borderColor: open ? 'hsl(0,0%,22%)' : 'hsl(0,0%,14%)',
          padding: '5px 8px', cursor: 'pointer',
          color: activeColor,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.07em',
          transition: 'all 0.15s', whiteSpace: 'nowrap', flexShrink: 0,
        }}
        onMouseEnter={e => { if (!open) { e.currentTarget.style.background = 'hsl(0,0%,8%)'; e.currentTarget.style.borderColor = 'hsl(0,0%,20%)'; } }}
        onMouseLeave={e => { if (!open) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'hsl(0,0%,14%)'; } }}
        title={label}
      >
        {activeLocation
          ? <MapPin size={12} weight="fill" color={activeColor} />
          : <Globe size={12} weight="regular" color={activeColor} />
        }
        <span className="loc-label">{label}</span>
        <CaretDown size={10} className="loc-caret" style={{ transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none', color: 'hsl(0,0%,40%)' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 36, left: 0, minWidth: 220,
          background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,18%)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.6)', zIndex: 200,
        }}>
          {/* All Locations — global roles only */}
          {isGlobal && (
            <button
              onClick={() => { switchLocation(null); setOpen(false); }}
              style={{
                width: '100%', padding: '9px 14px', background: !activeLocation ? 'hsl(0,0%,10%)' : 'transparent',
                border: 'none', borderBottom: '1px solid hsl(0,0%,11%)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                fontFamily: "'JetBrains Mono', monospace", fontSize: 10, textAlign: 'left',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => { if (activeLocation) e.currentTarget.style.background = 'hsl(0,0%,9%)'; }}
              onMouseLeave={e => { if (activeLocation) e.currentTarget.style.background = 'transparent'; }}
            >
              <Globe size={13} color={!activeLocation ? 'hsl(197,100%,48%)' : 'hsl(0,0%,50%)'} />
              <span style={{ color: !activeLocation ? 'hsl(197,100%,48%)' : 'hsl(0,0%,70%)' }}>All Locations</span>
              {!activeLocation && (
                <span style={{ marginLeft: 'auto', fontSize: 8, color: 'hsl(197,100%,48%)', background: 'hsl(197,40%,12%)', padding: '1px 5px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>ACTIVE</span>
              )}
            </button>
          )}

          {/* Location list */}
          {locations.map(loc => {
            const isActive = activeLocation?.code === loc.code;
            const color = TYPE_COLOR[loc.type] || 'hsl(0,0%,55%)';
            return (
              <button
                key={loc.code}
                onClick={() => { switchLocation(loc.code); setOpen(false); }}
                style={{
                  width: '100%', padding: '9px 14px',
                  background: isActive ? 'hsl(0,0%,10%)' : 'transparent',
                  border: 'none', borderBottom: '1px solid hsl(0,0%,11%)',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 10, textAlign: 'left',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'hsl(0,0%,9%)'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                <Buildings size={13} color={color} weight={isActive ? 'fill' : 'regular'} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: isActive ? color : 'hsl(0,0%,75%)', fontWeight: isActive ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {TYPE_ICON[loc.type] || '📍'} {loc.name}
                  </div>
                  <div style={{ fontSize: 8, color: 'hsl(0,0%,38%)', marginTop: 1, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                    {loc.code} · {loc.type}
                  </div>
                </div>
                {isActive && (
                  <span style={{ fontSize: 8, color, background: `${color}18`, padding: '1px 5px', textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0 }}>ACTIVE</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
